import React, { useState, useEffect, useRef } from 'react';
import FacturasAttachments from './FacturasAttachments';

interface ComprobanteData {
  tipo: string | null;
  letra: string | null;
  punto_venta: string | null;
  numero: string | null;
  fecha: string | null;
}

interface EmisorData {
  cuit: string | null;
  razon_social: string | null;
  condicion_iva: string | null;
}

interface ReceptorData {
  cuit: string | null;
}

interface ImportesData {
  neto: number | null;
  iva_21: number | null;
  iva_105: number | null;
  otros_impuestos: number | null;
  total: number | null;
}

interface AfipData {
  cae: string | null;
  cae_vencimiento: string | null;
}

interface ItemData {
  concepto: string | null;
  cantidad: number | null;
  precio_unit: number | null;
  subtotal: number | null;
}

export interface FacturaData {
  comprobante: ComprobanteData;
  emisor: EmisorData;
  receptor: ReceptorData;
  importes: ImportesData;
  afip: AfipData;
  items: ItemData[];
}

interface FacturaFormProps {
  initialData?: FacturaData;
  isLoading?: boolean;
  isDataLoaded?: boolean;
  validationErrors?: string[];
  onSubmit: (data: FacturaData) => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
  facturaId: string;
  readOnly?: boolean;
}

export const FacturaForm: React.FC<FacturaFormProps> = ({
  initialData,
  isLoading = false,
  isDataLoaded = false,
  validationErrors = [],
  onSubmit,
  onCancel,
  showCancelButton = false,
  facturaId,
  readOnly = false
}) => {
  const [items, setItems] = useState<ItemData[]>(
    initialData?.items || [{ concepto: null, cantidad: null, precio_unit: null, subtotal: null }]
  );
  const [formData, setFormData] = useState<FacturaData | null>(initialData || null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setItems(initialData.items && initialData.items.length > 0 ? initialData.items : [{ concepto: null, cantidad: null, precio_unit: null, subtotal: null }]);
    }
  }, [initialData]);

  const hasFieldError = (fieldName: string): boolean => {
    return validationErrors.some(error => error.toLowerCase().includes(fieldName.toLowerCase()));
  };

  const cleanCuit = (cuit: string | null): string | null => {
    if (!cuit) return null;
    return cuit.toString().replace(/[-\s]/g, '');
  };

  const handleAddItem = () => {
    setItems([...items, { concepto: null, cantidad: null, precio_unit: null, subtotal: null }]);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleUpdateItem = (index: number, field: keyof ItemData, value: string) => {
    const newItems = [...items];
    if (field === 'concepto') {
      newItems[index][field] = value || null;
    } else {
      newItems[index][field] = value ? parseFloat(value) : null;
    }
    setItems(newItems);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    const formDataObj = new FormData(formRef.current);

    const data: FacturaData = {
      comprobante: {
        tipo: formDataObj.get('comprobante.tipo') as string || null,
        letra: formDataObj.get('comprobante.letra') as string || null,
        punto_venta: formDataObj.get('comprobante.punto_venta') as string || null,
        numero: formDataObj.get('comprobante.numero') as string || null,
        fecha: formDataObj.get('comprobante.fecha') as string || null
      },
      emisor: {
        cuit: cleanCuit(formDataObj.get('emisor.cuit') as string),
        razon_social: formDataObj.get('emisor.razon_social') as string || null,
        condicion_iva: formDataObj.get('emisor.condicion_iva') as string || null
      },
      receptor: {
        cuit: cleanCuit(formDataObj.get('receptor.cuit') as string)
      },
      importes: {
        neto: formDataObj.get('importes.neto') ? parseFloat(formDataObj.get('importes.neto') as string) : null,
        iva_21: formDataObj.get('importes.iva_21') ? parseFloat(formDataObj.get('importes.iva_21') as string) : null,
        iva_105: formDataObj.get('importes.iva_105') ? parseFloat(formDataObj.get('importes.iva_105') as string) : null,
        otros_impuestos: formDataObj.get('importes.otros_impuestos') ? parseFloat(formDataObj.get('importes.otros_impuestos') as string) : null,
        total: formDataObj.get('importes.total') ? parseFloat(formDataObj.get('importes.total') as string) : null
      },
      afip: {
        cae: formDataObj.get('afip.cae') as string || null,
        cae_vencimiento: formDataObj.get('afip.cae_vencimiento') as string || null
      },
      items: items
    };

    onSubmit(data);
  };

  return (
    <>
      <form ref={formRef} onSubmit={handleSubmit} className="factura-form">
        <fieldset className={isLoading ? 'loading' : isDataLoaded ? 'data-loaded' : ''}>
          {/* Comprobante */}
          <fieldset className="form-card">
            <legend><span className="legend-icon">🧾</span> Comprobante</legend>
            <div className="form-row-comprobante">
              <label>
                Tipo:
                <select name="comprobante.tipo" defaultValue={formData?.comprobante?.tipo || ''} className={hasFieldError('tipo') ? 'field-error' : ''} disabled={readOnly}>
                  <option value="">Seleccionar</option>
                  <option value="Factura">Factura</option>
                  <option value="Nota de Crédito">Nota de Crédito</option>
                  <option value="Nota de Débito">Nota de Débito</option>
                  <option value="Recibo">Recibo</option>
                </select>
              </label>
              <label>
                Letra:
                <select name="comprobante.letra" defaultValue={formData?.comprobante?.letra || ''} className={hasFieldError('letra') ? 'field-error' : ''} disabled={readOnly}>
                  <option value="">Seleccionar</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="E">E</option>
                  <option value="M">M</option>
                  <option value="T">T</option>
                </select>
              </label>
              <label>
                Punto venta:
                <input type="text" name="comprobante.punto_venta" defaultValue={formData?.comprobante?.punto_venta || ''} placeholder="0001" className={hasFieldError('punto venta') ? 'field-error' : ''} disabled={readOnly} />
              </label>
              <label>
                Número:
                <input type="text" name="comprobante.numero" defaultValue={formData?.comprobante?.numero || ''} placeholder="00000001" className={hasFieldError('número') && hasFieldError('comprobante') ? 'field-error' : ''} disabled={readOnly} />
              </label>
              <label>
                Fecha:
                <input type="date" name="comprobante.fecha" defaultValue={formData?.comprobante?.fecha || ''} className={hasFieldError('fecha') ? 'field-error' : ''} disabled={readOnly} />
              </label>
            </div>
          </fieldset>

          {/* Emisor */}
          <fieldset className="form-card">
            <legend><span className="legend-icon">🏢</span> Emisor</legend>
            <div className="form-row-emisor">
              <label>
                CUIT:
                <input type="text" name="emisor.cuit" defaultValue={formData?.emisor?.cuit || ''} pattern="[0-9]{11}" maxLength={11} title="Exactamente 11 dígitos numéricos" className={hasFieldError('cuit') && hasFieldError('emisor') ? 'field-error' : ''} disabled={readOnly} />
              </label>
              <label>
                Condición IVA:
                <select name="emisor.condicion_iva" defaultValue={formData?.emisor?.condicion_iva || ''} className={hasFieldError('condición iva') ? 'field-error' : ''} disabled={readOnly}>
                  <option value="">Seleccionar</option>
                  <option value="IVA Responsable Inscripto">IVA Responsable Inscripto</option>
                  <option value="IVA Responsable No Inscripto">IVA Responsable No Inscripto</option>
                  <option value="IVA Sujeto Exento">IVA Sujeto Exento</option>
                  <option value="Consumidor Final">Consumidor Final</option>
                  <option value="Responsable Monotributo">Responsable Monotributo</option>
                  <option value="Proveedor del Exterior">Proveedor del Exterior</option>
                  <option value="Cliente del Exterior">Cliente del Exterior</option>
                  <option value="IVA Liberado - Ley 19.640">IVA Liberado - Ley 19.640</option>
                  <option value="Monotributista Social">Monotributista Social</option>
                  <option value="IVA No Alcanzado">IVA No Alcanzado</option>
                </select>
              </label>
              <label>
                Razón Social:
                <input type="text" name="emisor.razon_social" defaultValue={formData?.emisor?.razon_social || ''} className={hasFieldError('razón social') ? 'field-error' : ''} disabled={readOnly} />
              </label>
            </div>
          </fieldset>

          {/* Receptor y AFIP */}
          <fieldset className="form-card">
            <legend><span className="legend-icon">📋</span> Receptor y AFIP</legend>
            <div className="form-row-receptor-afip">
              <label>
                CUIT Receptor:
                <input type="text" name="receptor.cuit" defaultValue={formData?.receptor?.cuit || ''} pattern="[0-9]{11}" maxLength={11} title="Exactamente 11 dígitos numéricos" disabled={readOnly} />
              </label>
              <label>
                CAE:
                <input type="text" name="afip.cae" defaultValue={formData?.afip?.cae || ''} className={hasFieldError('cae') && !hasFieldError('vencimiento') ? 'field-error' : ''} disabled={readOnly} />
              </label>
              <label>
                Vencimiento CAE:
                <input type="date" name="afip.cae_vencimiento" defaultValue={formData?.afip?.cae_vencimiento || ''} className={hasFieldError('vencimiento cae') ? 'field-error' : ''} disabled={readOnly} />
              </label>
            </div>
          </fieldset>

          {/* Items */}
          <fieldset className="form-card">
            <legend><span className="legend-icon">📦</span> Items / Conceptos</legend>
            <div className="items-container">
              {items.map((item, index) => (
                <div key={index} className="item">
                  <div className="form-row-item">
                    <label>
                      Concepto:
                      <input
                        type="text"
                        value={item.concepto || ''}
                        onChange={(e) => handleUpdateItem(index, 'concepto', e.target.value)}
                        disabled={readOnly}
                      />
                    </label>
                    <label>
                      Cantidad:
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.cantidad || ''}
                        onChange={(e) => handleUpdateItem(index, 'cantidad', e.target.value)}
                        disabled={readOnly}
                      />
                    </label>
                    <label>
                      Precio unit.:
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.precio_unit || ''}
                        onChange={(e) => handleUpdateItem(index, 'precio_unit', e.target.value)}
                        disabled={readOnly}
                      />
                    </label>
                    <label>
                      Subtotal:
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.subtotal || ''}
                        onChange={(e) => handleUpdateItem(index, 'subtotal', e.target.value)}
                        disabled={readOnly}
                      />
                    </label>
                    {!readOnly && (
                      <button type="button" className="btn-remove-item" onClick={() => handleRemoveItem(index)} title="Eliminar item">🗑️</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {!readOnly && (
              <button type="button" className="btn-secondary" onClick={handleAddItem}>Agregar Item</button>
            )}
          </fieldset>

          {/* Importes */}
          <fieldset className="form-card">
            <legend><span className="legend-icon">💰</span> Importes</legend>
            <div className="form-row-importes">
              <label>
                Neto:
                <input type="number" name="importes.neto" defaultValue={formData?.importes?.neto || ''} step="0.01" min="0" className={hasFieldError('neto') ? 'field-error' : ''} disabled={readOnly} />
              </label>
              <label>
                IVA 21%:
                <input type="number" name="importes.iva_21" defaultValue={formData?.importes?.iva_21 || ''} step="0.01" min="0" disabled={readOnly} />
              </label>
              <label>
                IVA 10.5%:
                <input type="number" name="importes.iva_105" defaultValue={formData?.importes?.iva_105 || ''} step="0.01" min="0" disabled={readOnly} />
              </label>
              <label>
                Otros imp.:
                <input type="number" name="importes.otros_impuestos" defaultValue={formData?.importes?.otros_impuestos || ''} step="0.01" min="0" disabled={readOnly} />
              </label>
              <label>
                Total:
                <input type="number" name="importes.total" defaultValue={formData?.importes?.total || ''} step="0.01" min="0" className={hasFieldError('total') ? 'field-error' : ''} disabled={readOnly} />
              </label>
            </div>
          </fieldset>
        </fieldset>

        {!readOnly && (
          <div className="form-actions">
            {showCancelButton && onCancel && (
              <button type="button" onClick={onCancel} className="btn-cancel">Cancelar</button>
            )}
            <button type="submit" className="btn-submit">Guardar cambios</button>
          </div>
        )}
      </form>
      <FacturasAttachments
        facturaId={facturaId}
        maxFileSize={50 * 1024 * 1024}
        maxAttachments={20}
        onAttachmentAdded={(attachment) => {
          console.log('Adjunto agregado:', attachment);
        }}
        onAttachmentDeleted={(id) => {
          console.log('Adjunto eliminado:', id);
        }}
        onError={(error) => {
          console.error('Error:', error);
        }}
      />
    </>
  );
};

export default FacturaForm;
